import { useAtomValue } from "jotai";
import type { FunctionComponent } from "react";
import { useEffect } from "react";

import { type Edition, editionOptions, editions } from "shared/lib/edition";
import { notEmpty } from "shared/lib/utils";

import { appAtom } from "scmp-app/lib/app/atoms";

import {
  EditionGroup,
  EditionText,
  StyledBaseLink,
  StyledNotificationItem,
  Text,
  UnknownGeoContainer,
  YesButtonLink,
} from "./styles";

export type Props = {
  editionFromRoute: Edition;
  onClose?: () => void;
  onEditionSet: (key?: Edition) => void;
  suggestEdition: Edition;
};

export const EditionInvitation: FunctionComponent<Props> = ({
  editionFromRoute,
  onClose,
  onEditionSet,
  suggestEdition,
}) => {
  const { navigatorInfo } = useAtomValue(appAtom);
  const isUnknownLocation = !notEmpty(navigatorInfo?.country);

  // Set the edition when it's shown
  useEffect(() => {
    onEditionSet(editionFromRoute);
  }, [editionFromRoute, onEditionSet]);

  const renderContent = () => {
    if (isUnknownLocation)
      return (
        <UnknownGeoContainer>
          <Text>Please select your preferred edition:</Text>
          <EditionGroup>
            {editionOptions.map(edition => (
              <StyledBaseLink
                key={edition.value}
                onClick={() => {
                  onEditionSet(edition.value);
                  onClose?.();
                }}
                pathname={edition.href}
              >
                {edition.label}
              </StyledBaseLink>
            ))}
          </EditionGroup>
        </UnknownGeoContainer>
      );
    return (
      <>
        <Text>
          {"Would you like to switch to the "}
          <EditionText>{editions[suggestEdition].label}</EditionText>
          {" edition?"}
        </Text>
        <YesButtonLink
          onClick={() => {
            onEditionSet();
            onClose?.();
          }}
          pathname={editions[suggestEdition].href}
        >
          YES
        </YesButtonLink>
      </>
    );
  };

  return (
    <StyledNotificationItem
      onClose={() => {
        onEditionSet(editionFromRoute);
        onClose?.();
      }}
    >
      {renderContent()}
    </StyledNotificationItem>
  );
};

EditionInvitation.displayName = "EditionInvitation";
